import React, { useState, useMemo } from "react";
import Select from "react-select";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useUser } from "../contexts/UserContext";
import { useInvoiceContext } from "../contexts/InvoiceContext";
import { useAdminContext } from "../contexts/AdminContext";
import { toast } from "react-toastify";
import Loader from "../utils/Loader";
import TemplateSection from "../components/Invoice/TemplateSection";
import InvoiceList from "../components/Invoice/InvoiceList";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import SendInvoiceModal from "../components/Invoice/SendInvoiceModal";
import generateNewInvoiceTypePDF from "../components/AdditionalInvoiceType/GenerateNewInvoiceType";
import { mailForm, findExistingClient } from "../utils/Utility";
import { useDownloadModal } from "../contexts/DownloadModalContext";
import AppLauncherMenu from "../utils/AppLauncherMenu";
import {
  downloadCloudApi,
  downloadAndZipCloudApi,
  formatOldUrl,
  deleteCloudApi,
  decryptData,
} from "../utils/cloudApi";
import {
  selectStyles,
  recordsApps,
  receiptsApps,
} from "../components/Constants";
import PdfPreviewModal from "../components/PreviewPdfModal";

const AppDashboard = () => {
  const {
    invoices,
    setInvoices,
    templates,
    setTemplates,
    loading,
    setLoading,
  } = useInvoiceContext();

  const navigate = useNavigate();
  const { group, type } = useParams();
  const { userData, invoiceClients, privateKeyInMemory } = useUser();
  const { setIsOpen, setProgress } = useDownloadModal();

  // Admin context with centralized state
  const {
    adminData,
    selectedUserId,
    isViewingOtherUser,
    handleUserSelection,
    getSelectedUserName,
    getAvailableUsers,
    canEdit,
    apLoading,
    selectedUserData,
    deleteUserInvoice,
    deleteUserTemplate,
    addUserTemplate,
    updateUserTemplate,
  } = useAdminContext();

  const currentInvoices = isViewingOtherUser
    ? adminData.userInvoices
    : invoices;

  const currentTemplates = isViewingOtherUser
    ? adminData.userTemplates
    : templates;

  const currentUserClients = isViewingOtherUser
    ? adminData.userClients
    : invoiceClients;

  const [loadingText, setLoadingText] = useState("Loading Invoices");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState("");
  const [navbarButtons, setNavbarButtons] = useState([]);

  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [defaultSubject, setDefaultSubject] = useState("");

  const [showPreviewInvoice, setShowPreviewInvoice] = useState(false);
  const [showPreviewNewInvoice, setShowPreviewNewInvoice] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const category = type;

  const selectOptions = useMemo(() => {
    const options = [{ value: "default", label: "My Dashboard" }];

    getAvailableUsers().forEach((user) => {
      options.push({
        value: user.uid,
        label: user.name,
      });
    });

    return options;
  }, [getAvailableUsers]);

  const selectedOption = useMemo(() => {
    return (
      selectOptions.find((option) => option.value === selectedUserId) ||
      selectOptions[0]
    );
  }, [selectOptions, selectedUserId]);

  const appRoutes = (key) => {
    const allApps = [...receiptsApps, ...recordsApps];
    const currApp = allApps.find((app) => app.key === key);

    return currApp.path;
  };

  // Handle dropdown change
  const handleReactSelectChange = async (selectedOption) => {
    if (!selectedOption) return;

    setLoading(true);
    setLoadingText("Loading user data...");

    const result = await handleUserSelection(selectedOption.value);

    if (!result.success) {
      if (result.permissionDenied) {
        toast.error(
          result.message || "Access denied to invoice system features"
        );
      } else {
        toast.error("Failed to load user data");
      }
    } else if (result.firstAccessibleFeature) {
      const feature = result.firstAccessibleFeature;

      const path = appRoutes(feature.key);
      if (path) {
        navigate(path);
      } else {
        console.warn(`No path found for key: ${feature.key}`);
      }
    }

    setLoading(false);
    setLoadingText("Loading Invoices");
  };

  const pageTitles = {
    receipts: {
      invoices: "Invoices",
      noc: "Notice of Charges",
      acknowledgement: "Acknowledgment Receipt",
    },
    records: {
      transmittal: "Transmittal Letter",
      "secretarial-records": "Secretarial Records",
      "process-server-logs": "Process Server Logs",
      "exec-admin": "Executive & Administrative",
      "case-brief": "Case Brief",
      "legal-advice": "Legal Advice",
      "sec-res": "Secretarial Resolutions",
    },
  };

  const pageTitle = pageTitles[group]?.[type] || "Invoices";

  const handleOpenTemplate = (template = null) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handleSaveTemplate = (updatedTemplate) => {
    if (isViewingOtherUser) {
      const existingTemplate = currentTemplates.find(
        (template) => template.id === updatedTemplate.id
      );

      if (existingTemplate) {
        updateUserTemplate(updatedTemplate.id, updatedTemplate);
      } else {
        addUserTemplate(updatedTemplate);
      }
    } else {
      setTemplates((prevTemplates) => {
        const existingTemplateIndex = prevTemplates.findIndex(
          (template) => template.id === updatedTemplate.id
        );
        if (existingTemplateIndex !== -1) {
          return prevTemplates.map((template) =>
            template.id === updatedTemplate.id ? updatedTemplate : template
          );
        } else {
          return [...prevTemplates, updatedTemplate];
        }
      });
    }
  };

  const handleTemplateClick = (template) => {
    if (category === "acknowledgement" || category === "sec-res") {
      let ackType;

      if (template.notarizationStatus === "notarized") {
        const notarizationType = template.notarizationType;

        // Determine the ackType based on category and notarization type
        if (category === "sec-res") {
          // For Secretary's Resolutions
          if (notarizationType === "jurat") {
            ackType = category;
          } else if (notarizationType === "non-jurat") {
            ackType = category;
          } else {
            ackType = notarizationType;
          }
        } else if (category === "acknowledgement") {
          // For regular acknowledgements
          if (notarizationType === "jurat") {
            ackType = category;
          } else if (notarizationType === "non-jurat") {
            ackType = category;
          } else {
            ackType = notarizationType;
          }
        }
      } else {
        // Not notarized yet
        ackType = template.notarizationStatus;
      }

      navigate(`/new-doc`, {
        state: {
          invoiceType: template.tempType,
          ackType: ackType,
          template,
          adminContext: isViewingOtherUser
            ? {
                selectedUserId,
                isViewingOtherUser,
                selectedUserData,
              }
            : null,
        },
      });
    } else if (category === "transmittal" || category === "legal-advice") {
      navigate(`/new-doc`, {
        state: {
          invoiceType: template.tempType,
          template,
          adminContext: isViewingOtherUser
            ? {
                selectedUserId,
                isViewingOtherUser,
                selectedUserData,
              }
            : null,
        },
      });
    } else {
      // Pass admin context info in navigation state
      navigate(`/invoice-form`, {
        state: {
          template,
          ackType: template.ackType,
          adminContext: isViewingOtherUser
            ? {
                selectedUserId,
                isViewingOtherUser,
                selectedUserData,
              }
            : null,
        },
      });
    }
  };

  const handleSpecialReceipt = (template = null) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);

    // console.log("Modal open");
  };

  const handleRowClick = (invoiceNo, id, ackType) => {
    const navigationState = {
      adminContext: isViewingOtherUser
        ? {
            selectedUserId,
            isViewingOtherUser,
            selectedUserData,
          }
        : null,
    };

    if (invoiceNo.startsWith("AR") || invoiceNo.startsWith("SR")) {
      // AR = Acknowledgement Receipt, SR = Secretarial Resolution
      navigate(`/edit-doc/${id}`, {
        state: {
          invoiceType: invoiceNo.startsWith("AR") ? "ack" : "secres",
          ackType: ackType,
          ...navigationState,
        },
      });
    } else if (invoiceNo.startsWith("TL") || invoiceNo.startsWith("LA")) {
      // TL = Transmittal Letter, LA = Legal Advice
      navigate(`/edit-doc/${id}`, {
        state: {
          invoiceType: invoiceNo.startsWith("TL") ? "trans" : "legal",
          ackType: ackType,
          ...navigationState,
        },
      });
    } else {
      navigate(`/invoice-form/${id}`, { state: navigationState });
    }
  };

  const requestDelete = (type, id) => {
    setDeleteType(type);
    setDeleteTarget(id);
    setConfirmDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      setLoadingText("Deleting...");

      if (deleteType === "invoice") {
        await deleteDoc(doc(db, "invoices", deleteTarget));

        if (isViewingOtherUser) {
          deleteUserInvoice(deleteTarget);
        } else {
          setInvoices((prev) =>
            prev.filter((item) => item.id !== deleteTarget)
          );
        }
        toast.success("Invoice deleted successfully!");
      } else if (deleteType === "template") {
        const templateDoc = currentTemplates.find((t) => t.id === deleteTarget);
        if (!templateDoc) {
          throw new Error(`Template ${deleteTarget} not found in state`);
        }

        const imagesToDelete = [
          templateDoc.logo,
          templateDoc.gcashQr,
          templateDoc.gcashQr2,
        ].filter(Boolean);

        await deleteDoc(doc(db, "invoiceTemplates", deleteTarget));

        if (isViewingOtherUser) {
          deleteUserTemplate(deleteTarget);
        } else {
          setTemplates((prev) =>
            prev.filter((item) => item.id !== deleteTarget)
          );
        }

        for (const url of imagesToDelete) {
          try {
            await deleteCloudApi(url, userData.uid);
          } catch (err) {
            console.error("Failed to delete template image:", url, err);
          }
        }

        toast.success("Template deleted successfully!");
      } else {
        const docuType =
          category === "noc"
            ? "Notice of Charges"
            : category === "transmittal"
            ? "Transmittal Letter"
            : "Acknowledgement Receipt";

        const invoiceDoc = currentInvoices.find(
          (inv) => inv.id === deleteTarget
        );
        if (!invoiceDoc) {
          throw new Error(`${docuType} ${deleteTarget} not found in state`);
        }

        await deleteDoc(doc(db, "invoices", deleteTarget));

        if (isViewingOtherUser) {
          deleteUserInvoice(deleteTarget);
        } else {
          setInvoices((prev) =>
            prev.filter((item) => item.id !== deleteTarget)
          );
        }

        toast.success(`${docuType} deleted successfully!`);
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item. Please try again.");
    } finally {
      setLoading(false);
      setLoadingText("");
      setConfirmDeleteModalOpen(false);
      setDeleteTarget(null);
      setDeleteType("");
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteModalOpen(false);
    setDeleteTarget(null);
    setDeleteType("");
  };

  const handleDownload = async (invoice) => {
    try {
      setLoading(true);
      setLoadingText("Downloading Invoice");

      const curInv = await decryptData(
        invoice,
        userData.uid,
        privateKeyInMemory
      );

      if (category === "invoices" || category === "noc") {
        const curUser = isViewingOtherUser ? selectedUserData : userData;

        const { blob } = await downloadCloudApi(
          curInv,
          curUser,
          currentUserClients,
          category
        );

        const curClient = findExistingClient(curInv.to, currentUserClients);
        const tempCid = curClient?.cid || null;

        const cid = tempCid ? tempCid : "CID000XX";
        const tempType = category === "noc" ? "" : "SQ-";
        const finalName = `${cid}_${tempType}${curInv.invoiceNo}.pdf`;

        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `${finalName}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } else if (["transmittal", "acknowledgement"].includes(category)) {
        let invoiceType = category === "transmittal" ? "trans" : "ack";
        const tempDate = formattedDate(curInv.date);

        const curClient = findExistingClient(curInv.to, currentUserClients);
        const tempCid = curClient?.cid || "CID000XX";

        await generateNewInvoiceTypePDF(
          curInv,
          curInv.headerImage,
          curInv.footer || null,
          tempCid,
          tempDate,
          invoiceType,
          "download"
        );
      }
    } catch (error) {
      console.error("Error downloading the PDF:", error);
      toast.error("Failed to download the PDF. Please try again.");
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  const handleEmail = async (invoice) => {
    const curInv = await decryptData(invoice, userData.uid, privateKeyInMemory);
    setSelectedInvoice(curInv);
    if (category === "invoices" || category === "noc") {
      setDefaultSubject(
        `${curInv.from.name} - ${curInv.header} [ #${curInv.invoiceNo} ]`
      );
    } else if (category === "transmittal") {
      setDefaultSubject(`${curInv.subject} - [ #${curInv.invoiceNo} ]`);
    } else if (category === "acknowledgement") {
      setDefaultSubject(`${curInv.subject} - [ #${curInv.invoiceNo} ]`);
    }
    setIsSendModalOpen(true);
  };

  const formattedDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const blobUrlToBase64 = async (blobUrl) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleSendEmail = async ({ subject, body, recipient }) => {
    setLoading(true);
    setLoadingText("Sending. Please wait");

    const emailBody = mailForm(body);

    try {
      let attachment = {
        fileName: `${selectedInvoice.header}_${selectedInvoice.invoiceNo}_invoice.pdf`,
      };

      if (category === "invoices" || category === "noc") {
        attachment.url = formatOldUrl(selectedInvoice.pdfUrl);
      } else if (["transmittal", "acknowledgement"].includes(category)) {
        let invoiceType = category === "transmittal" ? "trans" : "ack";
        const tempDate = formattedDate(selectedInvoice.date);
        const prepPdfUrl = await generateNewInvoiceTypePDF(
          selectedInvoice,
          selectedInvoice.headerImage,
          selectedInvoice.footer || null,
          tempDate,
          invoiceType,
          ""
        );
        attachment.fileData = await blobUrlToBase64(prepPdfUrl);
      }

      const response = await fetch(
        `${process.env.REACT_APP_FIREBASE_BASE_API}/sendEmail`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            data: {
              to: recipient.trim(),
              subject: subject.trim(),
              message: emailBody,
              attachment: attachment,
            },
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Server error. Please try again.");
      }

      const result = await response.json();
      toast.success(result.data?.message || "Email sent successfully!");
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setSelectedInvoice(null);
      setIsSendModalOpen(false);
      setLoading(false);
      setLoadingText("");
    }
  };

  const handleDownloadAll = async (sortedInvoices) => {
    setIsOpen(true);
    try {
      const curUser = isViewingOtherUser ? selectedUserData : userData;
      await downloadAndZipCloudApi(
        sortedInvoices,
        curUser,
        (pct) => setProgress(Math.floor(pct)),
        currentUserClients,
        category,
        privateKeyInMemory
      );
      setProgress(100);
    } catch (err) {
      console.error("Failed to download all", err);
    } finally {
      setIsOpen(false);
      setProgress(0);
    }
  };

  const handleViewDocument = async (inv) => {
    setSelectedInvoice(inv);

    // console.log(inv);

    if (category === "invoices" || category === "noc") {
      setShowPreviewInvoice(true);
    } else {
      setLoading(true);
      setLoadingText("Preparing PDF...");
      let invoiceType = category === "transmittal" ? "trans" : "ack";
      try {
        const curInv = await decryptData(inv, userData.uid, privateKeyInMemory);
        // console.log(curInv);
        const pdfUrl = await generateNewInvoiceTypePDF(
          curInv,
          curInv.headerImage,
          curInv.footer || null,
          "",
          formattedDate(curInv.date),
          invoiceType,
          "preview"
        );

        setPdfUrl(pdfUrl);
        setShowPreviewNewInvoice(true);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        setLoadingText("");
      }
    }
  };

  const closePreview = () => {
    if (category === "invoices" || category === "noc") {
      setShowPreviewInvoice(false);
    } else {
      setShowPreviewNewInvoice(false);
    }

    setSelectedInvoice(null);
  };

  return (
    <>
      <Helmet>
        <title>UP-Hub Tools - {pageTitle}</title>
      </Helmet>

      {/* Breadcrumb Navigation */}
      <nav className="w-full bg-white/10">
        <div className="px-3 py-4 max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex gap-1">
            {navbarButtons.map((btn) => (
              <button
                key={btn.label}
                onClick={btn.onClick}
                disabled={isViewingOtherUser && btn.requiresOwnership}
                className={`flex flex-row items-center border justify-center px-3 rounded shadow shadow-tier-secondary bg-tier-primary dark:bg-tier-primary-dark dark:border-gray-600 hover:bg-white/20 ${
                  isViewingOtherUser && btn.requiresOwnership
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <span className="material-symbols-outlined text-base">
                  {btn.icon}
                </span>
                <p className="ml-2 font-medium text-xs text-center hidden sm:block">
                  {btn.label}
                </p>
              </button>
            ))}
          </div>

          <div className="flex flex-row items-center gap-2">
            <div className="min-w-64">
              <Select
                value={selectedOption}
                onChange={handleReactSelectChange}
                options={selectOptions}
                styles={selectStyles}
                classNamePrefix="react-select"
                isSearchable={true}
                isLoading={apLoading}
                isDisabled={apLoading}
                placeholder="Select user..."
                noOptionsMessage={() => "No users found"}
                loadingMessage={() => "Loading users..."}
              />
            </div>
            <AppLauncherMenu />
          </div>
        </div>
      </nav>

      <div className="p-2">
        {(loading || apLoading) && <Loader text={loadingText} />}

        {/* Show indicator when viewing another user's data */}
        {isViewingOtherUser && (
          <div
            className={`mb-4 p-3 border-l-4 rounded-r-md ${
              isViewingOtherUser
                ? "bg-teal-100 dark:bg-teal-900 border-teal-500 text-teal-700 dark:text-teal-200"
                : "bg-teal-100 dark:bg-teal-900 border-teal-500 text-teal-700 dark:text-teal-200"
            }`}
          >
            <div className="flex items-center">
              <span className="material-symbols-outlined mr-2">
                {isViewingOtherUser ? "admin_panel_settings" : "visibility"}
              </span>
              <div>
                <p>
                  <span className="font-medium">
                    {isViewingOtherUser ? "Admin Mode: Managing " : "Viewing "}
                  </span>
                  <span className="font-bold text-lg dark:text-tier-secondary text-amber-600">
                    {getSelectedUserName()}
                  </span>
                  <span className="font-medium">'s invoices and templates</span>
                </p>
                <p className="text-sm mt-1 opacity-90">
                  {isViewingOtherUser
                    ? "Full admin access: You can view, create, edit, delete, download, and email."
                    : "Read-only mode: You can view, download, and email, but cannot create, edit, or delete."}
                </p>
              </div>
            </div>
          </div>
        )}

        {isSendModalOpen && (
          <SendInvoiceModal
            isOpen={isSendModalOpen}
            onClose={() => setIsSendModalOpen(false)}
            onSend={handleSendEmail}
            invoice={selectedInvoice}
            defaultSubject={defaultSubject}
            invoiceType={category}
          />
        )}

        <TemplateSection
          navigate={navigate}
          templates={currentTemplates}
          handleTemplateClick={handleTemplateClick}
          handleOpenTemplate={handleOpenTemplate}
          isModalOpen={isModalOpen}
          selectedTemplate={selectedTemplate}
          setIsModalOpen={setIsModalOpen}
          handleSaveTemplate={handleSaveTemplate}
          handleDeleteTemplate={(id) => requestDelete("template", id)}
          handleSpecialReceipt={handleSpecialReceipt}
          category={category}
          setNavbarButtons={setNavbarButtons}
          isViewingOtherUser={isViewingOtherUser}
          canEdit={canEdit()}
        />

        <InvoiceList
          invoices={currentInvoices}
          handleRowClick={handleRowClick}
          handleDownload={(invoice) => handleDownload(invoice)}
          handleViewDocument={(invoice) => handleViewDocument(invoice)}
          handleEmail={(invoice) => handleEmail(invoice)}
          handleDelete={(id, cat) => requestDelete(cat, id)}
          category={category}
          handleDownloadAll={(invoices) => handleDownloadAll(invoices)}
          isViewingOtherUser={isViewingOtherUser}
          canEdit={canEdit()}
        />

        <ConfirmDeleteModal
          isOpen={confirmDeleteModalOpen}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          title="Confirm Delete"
          message="Are you sure you want to delete this item? This action cannot be undone."
        />

        {(showPreviewInvoice || showPreviewNewInvoice) && (
          <PdfPreviewModal
            invoiceData={showPreviewInvoice ? selectedInvoice : null}
            pdfUrl={showPreviewNewInvoice ? pdfUrl : null}
            onClose={closePreview}
          />
        )}
      </div>
    </>
  );
};

export default AppDashboard;
